import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { ApiResponseModel } from '../../../models/core/api-response.model';
import { SimpleItemModel } from '../../../models/simple-item.model';

@Component({
    selector: 'sl-auto-complete',
    templateUrl: './auto-complete.component.html',
    styleUrls: ['./auto-complete.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AutoCompleteComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() placeholderKey: string;
    @Input() control?: AbstractControl;
    @Input() observable?: Observable<ApiResponseModel<SimpleItemModel[]>>;
    @Input() defaultSelectedId?: any;
    @Input() isDisabled = false;
    @Input() items: SimpleItemModel[] = [];

    @Output() readonly clear = new EventEmitter<void>();
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() readonly select = new EventEmitter<MatAutocompleteSelectedEvent>();

    filteredItems: SimpleItemModel[];
    textFormControl = new FormControl();

    ngOnInit(): void {
        if (!!this.control) {
            if (!!this.control.validator) {
                this.textFormControl.addValidators(this.control.validator);
            }
            const statusSub = this.control.statusChanges.subscribe(() => this.validate());
            this.addSubscriptions(statusSub);
        }

        if (!!this.observable) {
            this.observable.subscribe(r => {
                this.items = r.value;
                this.setupList();
            });
        } else {
            this.setupList();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes['defaultSelectedId'] || !!changes['control']) {
            if (!!changes['control'] && !!this.control) {
                const statusSub = this.control.statusChanges.subscribe(() => this.validate());
                this.addSubscriptions(statusSub);
            }

            this.setupValue();
        }
    }

    selectItem(event: MatAutocompleteSelectedEvent): void {
        this.setControlValue(event.option.id);
        this.select.emit(event);
    }

    clearItem(): void {
        this.textFormControl.setValue('');
        this.setControlValue(undefined);
        this.clear.emit();
    }

    validate(): void {
        if (!!this.control) {
            if (!this.control.value) {
                this.textFormControl.setValue('');
            }

            if (!!this.control.errors) {
                this.textFormControl.markAsDirty();
                this.textFormControl.markAsTouched();
                this.textFormControl.updateValueAndValidity();
                this.textFormControl.setErrors(this.control.errors);
            }
        }
    }

    private setupList(): void {
        if (!!this.defaultSelectedId || (!!this.control && !!this.control.value)) {
            this.setupValue();
        } else if (!!this.control) {
            const extValueChanges = this.control.valueChanges.subscribe(() => this.setupValue());
            this.addSubscriptions(extValueChanges);
        }

        const valChanges = this.textFormControl.valueChanges
            .pipe(startWith(''))
            .subscribe(
                (query: string) =>
                    (this.filteredItems =
                        query && query.length > 0
                            ? this.items.filter(c => !!c.label && c.label.toLowerCase().includes(query.toLowerCase()))
                            : this.items)
            );

        if ((!!this.control && this.control.disabled) || this.isDisabled) {
            this.textFormControl.disable();
        }

        this.addSubscriptions(valChanges);
    }

    private setupValue(): void {
        const id = this.defaultSelectedId || (!!this.control ? this.control.value : undefined);
        // eslint-disable-next-line eqeqeq
        const defaultItem = this.items.find(c => c.id == id);

        if (!!defaultItem) {
            this.textFormControl.setValue(defaultItem.label);
        } else if (!!this.textFormControl.value) {
            this.clearItem();
        }
    }

    private setControlValue(value?: any): void {
        if (!!this.control) {
            this.control.setValue(value);
            this.control.markAsDirty();
        }
    }
}
